import styled, { css } from "styled-components";
import { spacing } from "../../../design-system/tokens/spacing";
import { typographyMixin, fontWeights } from "../../../design-system/tokens/typography";
import { borderRadius } from "../../../design-system/tokens/borders";
import { rawColors } from "../../../design-system/tokens/colors";
import { shadows } from "../../../design-system/tokens/shadows";
import { controlSize } from "../../../design-system/tokens/controlSizes";
import { TemplateOptionIcon } from "../ExportStoryModal/ExportStoryModal.styles";

export const FormCard = styled.div`
  display: flex;
  flex-direction: column;
  background-color: transparent;

  /* Initial render: natural height so Send-now content is measured. After
     measurement, SendNowForm applies inline max-height to lock the frame
     across tab switches. FormScroll handles overflow inside. */
  min-height: 0;
  height: 100%;
  overflow: hidden;
`;

export const FormScroll = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[4]};
  padding: ${spacing[5]} ${spacing[6]};
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

export const SplitFieldRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${spacing[3]};
  align-items: start;
`;

export const Label = styled.label`
  ${typographyMixin("caption", 3)}
  font-weight: ${fontWeights.bold};
  color: ${rawColors.text.highEmphasis};
`;

export const Note = styled.p`
  margin: 0;
  ${typographyMixin("caption", 4)}
  color: ${rawColors.text.mediumEmphasis};
`;

export const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${spacing[2]};
`;

export const ToggleLabel = styled.span`
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const RadioRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[6]};
  flex-wrap: wrap;
`;

export const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[2]};
  cursor: pointer;
  ${typographyMixin("body", 3)}
  color: ${rawColors.text.highEmphasis};
`;

export const Radio = styled.span<{ $checked: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1.5px solid
    ${({ $checked }) => ($checked ? rawColors.primary[500] : rawColors.stroke[300])};
  ${({ $checked }) =>
    $checked &&
    css`
      &::after {
        content: "";
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background-color: ${rawColors.primary[500]};
      }
    `}
`;

export const HiddenRadio = styled.input.attrs({ type: "radio" })`
  position: absolute;
  opacity: 0;
  pointer-events: none;
`;

export const SegmentedRow = styled.div`
  /* grey track with a white active thumb (F18) */
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  width: 100%;
  max-width: 100%;
  height: ${controlSize.field.md};
  padding: ${spacing[1]};
  border-radius: ${borderRadius[10]};
  background-color: ${rawColors.grey[100]};
  box-sizing: border-box;
`;

export const SegmentButton = styled.button<{ $active: boolean }>`
  ${typographyMixin("caption", 3)}
  font-weight: ${fontWeights.semibold};
  box-sizing: border-box;
  flex: 1 1 auto;
  min-width: max-content;
  white-space: nowrap;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing[1.5]};
  text-align: center;
  padding: ${spacing[2]};
  border: none;
  border-radius: ${borderRadius.lg};
  cursor: pointer;
  ${({ $active }) =>
    $active
      ? css`
          background-color: ${rawColors.structural.white};
          color: ${rawColors.text.highEmphasis};
          box-shadow: ${shadows.elevation[1]};
        `
      : css`
          background-color: transparent;
          color: ${rawColors.text.mediumEmphasis};
        `}
`;

export const SegmentDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: ${spacing[4]};
  background-color: ${rawColors.stroke[200]};
  flex-shrink: 0;
`;

export const MessageTextarea = styled.textarea`
  min-height: ${spacing[16]};
  resize: vertical;
  padding: ${spacing[2]} ${spacing[3]};
  background-color: ${rawColors.structural.white};
  border: 1px solid ${rawColors.stroke[200]};
  border-radius: ${borderRadius.lg};
  ${typographyMixin("caption", 3)}
  color: ${rawColors.text.highEmphasis};
  font-family: inherit;

  &::placeholder {
    color: ${rawColors.text.lowEmphasis};
  }

  &:focus {
    outline: none;
    border-color: ${rawColors.primary[500]};
  }
`;

export const CharCount = styled.span<{ $overLimit?: boolean }>`
  ${typographyMixin("caption", 4)}
  align-self: flex-end;
  color: ${({ $overLimit }) =>
    $overLimit ? rawColors.accent.red500 : rawColors.text.lowEmphasis};
`;

/**
 * Theme colour chip in the Theme dropdown — the Export modal's template
 * swatch, shrunk to the compact dropdown's icon size.
 */
export const ThemeSwatch = styled(TemplateOptionIcon)`
  width: 1rem;
  height: 0.75rem;
`;
